<template>
  <!-- One palette, no per-module hue (spec §2): the badge is always an espresso
       square with a cream icon (ModuleBadge). Flat band, not a card of boxes
       (workspace-start M2): the header is the toolbar (actions live there, not
       in a wash row of their own), the number row is hairline-separated cells,
       and the module's own chart sits directly under it. -->
  <div
    data-testid="homebase-module-card"
    :data-module="moduleKey"
    class="flex flex-col gap-3 rounded-xl border bg-surface p-5 shadow-sm"
    :class="{ 'min-h-[120px]': !collapsed }"
  >
    <!-- Header row: module indicator (opens the module) on the left, the
         module's quick actions as icon buttons in the middle, collapse toggle
         on the right. Collapsing hides every content block below, actions
         included. -->
    <div class="flex items-center justify-between gap-2">
      <button
        type="button"
        class="group inline-flex items-center gap-1.5 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-ring disabled:cursor-default"
        :aria-label="t('homebase.goToModule', { module: badgeLabel })"
        :title="provisioning ? t('homebase.provisioning') : t('homebase.goToModule', { module: badgeLabel })"
        :disabled="provisioning"
        @click="emit('open')"
      >
        <ModuleBadge :module="moduleKey" :label="badgeLabel" variant="plain" />
        <LumiIcon
          v-if="!provisioning"
          name="next"
          class="h-3.5 w-3.5 -translate-x-1 text-tertiary opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100"
        />
      </button>
      <div class="flex items-center gap-1">
        <template v-if="!collapsed && !provisioning && visibleActions.length">
          <IconButton
            v-for="a in visibleActions"
            :key="a.key"
            variant="ghost"
            :label="lbl(a)"
            @click="emit('navigate', a.deepLink)"
          >
            <LumiIcon :name="actionIcon(a.icon)" stroke-width="1.5" />
          </IconButton>
          <Menu
            v-if="overflowActions.length"
            :items="overflowActions.map((a) => ({ key: a.key, label: lbl(a) }))"
            :label="t('homebase.moreActions')"
            align="end"
            size="md"
            @select="onOverflowSelect"
          />
        </template>
        <IconButton
          v-if="!provisioning"
          variant="ghost"
          size="sm"
          :aria-expanded="!collapsed"
          :label="collapsed ? t('homebase.expand', { module: badgeLabel }) : t('homebase.collapse', { module: badgeLabel })"
          @click="collapsed = !collapsed"
        >
          <LumiIcon name="expand" class="transition-transform" :class="collapsed ? '-rotate-90' : ''" />
        </IconButton>
      </div>
    </div>

    <template v-if="!collapsed">
    <!-- Just-added module still being provisioned by the worker (issue 213 F8):
         a non-ready tile (spinner + "wird eingerichtet") in place of any content,
         so the launcher shows it immediately instead of only after it goes live. -->
    <div
      v-if="provisioning"
      class="flex items-center gap-2.5 rounded-lg bg-surface-wash px-3 py-2.5 text-sm text-secondary"
    >
      <Spinner size="sm" />
      <span>{{ t('homebase.provisioning') }}</span>
    </div>
    <template v-else-if="dashboard">
      <!-- Key facts — ONE hairline-separated row of "my state" numbers (no wash
           tiles, workspace-start M2). A cell with a deepLink is the whole go-to
           control. The number stands alone: an attention fact used to repeat
           its own value in a coral pill beside it, the same count twice in two
           sizes (issue 756) — the Heute row's "Braucht mich" tile is where
           attention is summed up. Four per row, a fifth wraps; 2×2 under 640px. -->
      <div v-if="dashboard.keyFacts.length" class="grid grid-cols-4 divide-x divide-default max-[639px]:grid-cols-2">
        <component
          :is="f.deepLink ? 'button' : 'div'"
          v-for="f in dashboard.keyFacts"
          :key="f.key"
          :type="f.deepLink ? 'button' : undefined"
          class="flex min-w-0 flex-col items-start gap-0.5 px-3 py-1 text-left first:pl-0 last:pr-0 disabled:cursor-default"
          @click="f.deepLink && emit('navigate', f.deepLink)"
        >
          <span class="text-xl font-bold leading-none tabular-nums text-heading">{{ factValue(f) }}</span>
          <span class="truncate text-xs text-secondary">{{ lbl(f) }}</span>
        </component>
      </div>

      <!-- The module's own chart, directly under the number row, no wash box
           (workspace-start M2): the running week (Zeit) or the stacked bar
           (ATS pipeline, Azubi report books). -->
      <WeekProgressPanel v-if="dashboard.weekProgress" flat :progress="dashboard.weekProgress" />
      <BreakdownBar
        v-if="dashboard.breakdown"
        :breakdown="dashboard.breakdown"
        @navigate="(dl) => emit('navigate', dl)"
      />

      <!-- The module answered with an empty dashboard (a fresh workspace, or a
           member with nothing assigned yet). Same treatment as a panel with no
           dashboard at all, so no panel is ever a blank box (#364 F12). -->
      <EmptyState v-if="!hasDashboardContent" data-testid="panel-empty" :title="t('homebase.empty')" />
    </template>

    <!-- Static quicklinks — modules with no live dashboard (People, Admin).
         Two per row (each half-width) so a pair fills the panel; single column
         when the panel is narrow. -->
    <div v-else-if="quicklinks?.length" class="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
      <button
        v-for="q in quicklinks"
        :key="q.key"
        type="button"
        class="group flex w-full items-center gap-2.5 rounded-md bg-surface-wash px-3 py-2 text-left transition-colors hover:bg-accent-soft-2"
        @click="q.deepLink ? emit('navigate', q.deepLink) : emit('open')"
      >
        <component :is="q.icon" v-if="q.icon" class="h-5 w-5 shrink-0 text-tertiary group-hover:text-accent" stroke-width="1.5" />
        <span class="flex-1 truncate text-sm font-medium text-body">{{ q.label }}</span>
        <LumiIcon name="next" class="h-4 w-4 shrink-0 text-tertiary transition-colors group-hover:text-accent" />
      </button>
    </div>

    <!-- Still fetching → a skeleton that reserves the band's height, so nothing
         shifts once the dashboard arrives (Quality bar). Never the empty state:
         "nothing here" and "not asked yet" are different answers (#364 F12). -->
    <div v-else-if="loading" class="flex flex-col gap-3" aria-hidden="true">
      <div class="h-[52px] rounded-lg bg-surface-wash" />
      <div class="h-[96px] rounded-lg bg-surface-wash" />
    </div>

    <!-- The module did not answer — unreachable, or the member may not read this
         module's dashboard. Said as "cannot be shown right now", NOT as "you have
         no data": an error or a permission refusal must never be dressed up as an
         empty workspace (#364 F12). -->
    <p v-else-if="unavailable" data-testid="panel-unavailable" class="text-sm text-secondary">
      {{ t('homebase.unavailable') }}
    </p>

    <!-- Everything above declined: the module answered, with nothing in it. A
         panel is never a blank box — it says so, and the badge above still opens
         the module (#364 F12). -->
    <EmptyState v-else data-testid="panel-empty" :title="t('homebase.empty')" />
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { EmptyState, ModuleBadge, Menu, LumiIcon, type LumiModuleKey, type LumiIconName, Spinner, IconButton } from '@lumi/ui';
import BreakdownBar from './homebase/BreakdownBar.vue';
import WeekProgressPanel from './homebase/WeekProgressPanel.vue';
import { localizedLabel, type KeyFact, type ModuleDashboard, type QuickAction, type QuickLink } from '../utils/homebase';

// Map a quick-action's stable machine icon key (emitted by each module's
// user-dashboard) to a meaning of the icon vocabulary (ADR 0096). Unknown keys fall
// back to a neutral glyph so a module shipping a new action ahead of us still gets a
// leading icon.
const ACTION_ICONS: Record<string, LumiIconName> = {
  plus: 'add',
  'user-plus': 'new-hire',
  clipboard: 'task',
  'clipboard-check': 'approval',
  clock: 'booking',
  calendar: 'leave',
  users: 'people',
  check: 'approval',
  star: 'favourite',
  'pencil-square': 'write',
  sparkles: 'skill',
  inbox: 'inbox',
};
const actionIcon = (key: string): LumiIconName => ACTION_ICONS[key] ?? 'section';

const props = defineProps<{
  moduleKey: LumiModuleKey;
  dashboard: ModuleDashboard | null;
  loading: boolean;
  quicklinks?: QuickLink[];
  // A module the tenant just added that the worker is still provisioning (issue
  // 213 F8) — render a non-ready tile instead of content, and the badge can't open
  // it yet.
  provisioning?: boolean;
  // The module's dashboard could not be read at all (unreachable, or refused).
  // Kept apart from `dashboard: null` on purpose: an unanswered panel says so
  // instead of claiming the workspace is empty (#364 F12).
  unavailable?: boolean;
}>();

const emit = defineEmits<{
  open: [];
  navigate: [deepLink: string];
}>();
const { t, te, locale } = useI18n();

// A number reads in the UI's language, with at most one decimal: time-api
// sends "Stunden diese Woche" as 40.33, which a German page would otherwise
// print with an English point beside the chart's own "40,3". Strings (a
// module's preformatted value) stay as sent.
function factValue(f: KeyFact): string {
  return typeof f.value === 'number'
    ? new Intl.NumberFormat(locale.value, { maximumFractionDigits: 1 }).format(f.value)
    : f.value;
}

// Localize a key-fact / quick-action label by its machine key (recents/news stay raw).
// The dashboard's own `module` string is passed so a bare key (Azubi) normalises
// to its catalogue namespace (see `normalizeFactKey`).
const lbl = (item: KeyFact | QuickAction) => localizedLabel(item, t, te, props.dashboard?.module);
const badgeLabel = computed(() => (props.moduleKey === 'admin' ? props.moduleKey : t(`lumiModules.${props.moduleKey}.name`)));

// Header actions: up to four icon buttons, a fifth-plus overflow into the
// "Weitere" menu (workspace-start M2, Quality bar ruling). The recents/news
// boxes those actions used to sit beside are gone — a band's content is now
// facts + chart only.
const actions = computed(() => props.dashboard?.quickActions ?? []);
const visibleActions = computed(() => (actions.value.length > 4 ? actions.value.slice(0, 4) : actions.value));
const overflowActions = computed(() => (actions.value.length > 4 ? actions.value.slice(4) : []));
function onOverflowSelect(key: string) {
  const action = actions.value.find((a) => a.key === key);
  if (action) emit('navigate', action.deepLink);
}

// Does the dashboard the module returned actually carry anything to render? An
// envelope with every list empty renders exactly as much as no envelope at all,
// so both land on the same empty state (#364 F12). `clock` no longer renders
// here (it moved to the Heute row's Stempeluhr tile, workspace-start M2); recents
// and news no longer render here either (workspace-start M2) — neither counts
// toward "has content" any more.
const hasDashboardContent = computed(() => {
  const d = props.dashboard;
  if (!d) return false;
  return !!(d.keyFacts.length || d.quickActions.length || d.breakdown || d.weekProgress);
});

// Collapse state, persisted per module so a user's choice survives reloads.
const collapseKey = `homebase.collapsed.${props.moduleKey}`;
const collapsed = ref(false);
try {
  collapsed.value = localStorage.getItem(collapseKey) === '1';
} catch {
  /* storage unavailable — default expanded */
}
watch(collapsed, (v) => {
  try {
    localStorage.setItem(collapseKey, v ? '1' : '0');
  } catch {
    /* swallowed: storage unavailable; the fold state is just not remembered */
  }
});
</script>
